import { useGoogleSignIn, type UseGoogleSignInOptions } from '../login/useGoogleSignIn';
import { isGoogleSignInSupportedOnThisDevice } from '../login/nativeGoogleSignIn';
import type { LoginOption } from '../login/loginConstants';
import { useAuth } from '../auth/AuthContext';
import { translateLoginOption, useI18n, type MessageKey } from '../i18n';
import styles from '../pages/AuthLoginPortal.module.css';

function GoogleIcon() {
  return (
    <svg className={styles.socialIcon} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export type AuthGoogleSignInBlockProps = {
  loginType: LoginOption;
  introKey?: MessageKey;
  disabled?: boolean;
} & Pick<UseGoogleSignInOptions, 'registerPrefillOnMiss' | 'onRegisterPrefill'>;

export function AuthGoogleSignInBlock({
  loginType,
  introKey,
  disabled = false,
  registerPrefillOnMiss,
  onRegisterPrefill,
}: AuthGoogleSignInBlockProps) {
  const { t } = useI18n();
  const { isLoggedIn } = useAuth();
  const {
    isConfigured: googleConfigured,
    buttonMountRef: googleButtonMountRef,
    submitting: googleSubmitting,
    errorFriendly: googleErrorFriendly,
    errorDetails: googleErrorDetails,
    requestGoogleSignIn,
  } = useGoogleSignIn(loginType, { registerPrefillOnMiss, onRegisterPrefill });

  if (!isGoogleSignInSupportedOnThisDevice()) {
    return null;
  }

  if (!googleConfigured) {
    return (
      <div className={styles.googleSignInBlock}>
        <button
          type="button"
          className={styles.socialBtnGoogle}
          disabled
          title={t('auth.portal.googleNotConfigured')}
        >
          <GoogleIcon />
          <span>{t('auth.portal.google')}</span>
        </button>
        <p className={styles.googleSetupHint}>{t('auth.portal.googleNotConfigured')}</p>
      </div>
    );
  }

  const introText = introKey
    ? t(introKey, { type: translateLoginOption(loginType, t) })
    : null;

  return (
    <div className={styles.googleSignInBlock}>
      {introText ? <p className={styles.googleRoleHint}>{introText}</p> : null}
      <button
        type="button"
        className={styles.socialBtnGoogleActive}
        onClick={() => void requestGoogleSignIn()}
        disabled={disabled || googleSubmitting || isLoggedIn}
        aria-busy={googleSubmitting}
      >
        <GoogleIcon />
        <span>{googleSubmitting ? t('auth.portal.googleSigningIn') : t('auth.portal.google')}</span>
      </button>
      <div ref={googleButtonMountRef} className={styles.googleButtonMountHidden} aria-hidden="true" />
      {googleErrorFriendly ? (
        <div className={styles.failureBlock} role="alert">
          <p className={styles.friendlyError}>{googleErrorFriendly}</p>
          {googleErrorDetails ? (
            <details className={styles.errorDetails}>
              <summary className={styles.errorDetailsSummary}>{t('common.technicalDetails')}</summary>
              <pre className={styles.technicalPre}>{googleErrorDetails}</pre>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
